// Engine API: seity onboarding / unlock (beta-onboarding keystone).
//
// Backed by ~/Dev/seitiate (PR #76): the per-tenant seity passphrase + recovery
// lifecycle. Reuses the shared engineFetchJson client (auth headers, 401 ->
// notifyUnauthorized, FastAPI error parsing) — no new HTTP plumbing.
//
// SECURITY: the plaintext passphrase / recovery key flow through request bodies
// only. They are never persisted, logged, or placed in query params. The
// recovery_key is returned by the engine exactly once (on first_use unlock) and
// must be surfaced to the user a single time.

import { engineFetchJson } from '@/lib/engineHttp';

export interface SeityStatus {
  /** True when the seity exists but is currently locked (passphrase required). */
  locked: boolean;
  /** True once a passphrase has ever been set. False => first-use setup flow. */
  initialized: boolean;
  /** True when a recovery key has been provisioned for this seity. */
  has_recovery: boolean;
  /**
   * True only for a genuine creator tenant that may hold a responsibility bond.
   * Operator / admin / director command-center logins are NOT bond-eligible and
   * must never be shown the first-run seity onboarding wall (identity-login SSOT
   * §1.1 invariant #3). Optional for back-compat: a legacy engine that omits the
   * field is treated as eligible — the pre-fix behaviour.
   */
  bond_eligible?: boolean;
}

export interface SeityUnlockResponse {
  unlocked: boolean;
  /** True only on the very first unlock (passphrase set for the first time). */
  first_use: boolean;
  /**
   * The one-time recovery key. Present ONLY when first_use === true. This is
   * the single moment the engine ever returns it — surface it to the user once,
   * require explicit confirmation, then drop it from memory.
   */
  recovery_key?: string | null;
}

export interface SeityRecoverResponse {
  unlocked: boolean;
  /** True when the passphrase was successfully rotated to the new value. */
  rotated: boolean;
}

export interface SeityRecoverByEmailResponse {
  ok: boolean;
  /**
   * Generic, non-enumerating message. The engine returns it identically whether
   * or not the email matched an account or the recovery key verified — so the
   * response never reveals which emails are registered. The user learns whether
   * recovery actually worked by whether their new passphrase unlocks the seity.
   */
  message: string;
}

/** GET /api/v1/seity/status — current lock / init / recovery state. */
export function getSeityStatus(): Promise<SeityStatus> {
  return engineFetchJson<SeityStatus>('/seity/status');
}

/**
 * POST /api/v1/seity/unlock — unlock (or, on first use, set) the seity passphrase.
 * On first_use the response carries the one-time recovery_key.
 */
export function unlockSeity(passphrase: string): Promise<SeityUnlockResponse> {
  return engineFetchJson<SeityUnlockResponse>('/seity/unlock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ passphrase }),
  });
}

/**
 * POST /api/v1/seity/recover — recover a forgotten passphrase using the recovery
 * key, rotating to a new passphrase.
 */
export function recoverSeity(
  recoveryKey: string,
  newPassphrase: string,
): Promise<SeityRecoverResponse> {
  return engineFetchJson<SeityRecoverResponse>('/seity/recover', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recovery_key: recoveryKey, new_passphrase: newPassphrase }),
  });
}

/**
 * POST /api/v1/seity/recover-by-email — PRE-AUTH seity passphrase recovery
 * (identity-login SSOT Stage 0). For a locked-out user who cannot log in: reset
 * the seity passphrase with the show-once recovery key before authenticating.
 *
 * This is unauthenticated by design — it does not send (and does not require) a
 * bearer token. The engine returns a generic, non-enumerating response, so a
 * resolved promise means "request accepted", NOT "recovery succeeded". The user
 * confirms success by signing in and unlocking with the new passphrase.
 */
export function recoverSeityByEmail(
  email: string,
  recoveryKey: string,
  newPassphrase: string,
): Promise<SeityRecoverByEmailResponse> {
  return engineFetchJson<SeityRecoverByEmailResponse>('/seity/recover-by-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      recovery_key: recoveryKey,
      new_passphrase: newPassphrase,
    }),
  });
}
